import React, { useState, useEffect, useCallback } from 'react';
import '../src/i18n';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { BrandingProvider } from './context/BrandingContext';
import { useAuth } from './context/AuthContext';
import { useBranding } from './context/BrandingContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardPage from './pages/DashboardPage';
import CategoryPage from './pages/CategoryPage';
import LoginPage from './pages/LoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import SalesStatisticsPage from './pages/admin/SalesStatisticsPage';
import ProductManagementPage from './pages/admin/ProductManagementPage';
import CategoryManagementPage from './pages/admin/CategoryManagementPage';
import AccessHistoryPage from './pages/admin/AccessHistoryPage';
import EditHistoryPage from './pages/admin/EditHistoryPage';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import DiagnosticPage from './pages/DiagnosticPage';
import Footer from './components/Footer';
import PopupManager from './components/PopupManager';
import TokenMigrationBanner from './components/TokenMigrationBanner';
import SessionWarningModal from './components/SessionWarningModal';

// Sistema de traducción mediante cookies de Google Translate
window.changeLanguage = function(lang) {
  console.log('🌐 Cambiando idioma a:', lang);
  
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Función para eliminar cookies de Google Translate
  const clearGoogleTranslateCookies = () => {
    const cookies = ['googtrans', 'googtrans-domain'];
    const hostname = window.location.hostname;
    
    cookies.forEach(cookieName => {
      // Limpieza básica
      document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
      document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + hostname;
      document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + hostname;
      
      // Para subdominios como newtarifario.chirak.cl
      if (!isLocalhost) {
        // Extraer dominio raíz (chirak.cl de newtarifario.chirak.cl)
        const parts = hostname.split('.');
        if (parts.length >= 2) {
          const rootDomain = parts.slice(-2).join('.'); // chirak.cl
          console.log('🧹 Limpiando cookies en dominio raíz:', rootDomain);
          
          // Limpiar en el dominio raíz
          document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + rootDomain;
          document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + rootDomain;
          document.cookie = cookieName + '=; max-age=0; path=/; domain=' + rootDomain;
          document.cookie = cookieName + '=; max-age=0; path=/; domain=.' + rootDomain;
        }
        
        // Intentar sin dominio también
        document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; max-age=0; path=/';
      }
    });
  };
  
  if (lang === 'es') {
    // Volver a español
    console.log('🔄 Volviendo a español...');
    clearGoogleTranslateCookies();
    
    setTimeout(() => {
      clearGoogleTranslateCookies();
      
      // Mantener la URL completa incluyendo hash y parámetros
      const fullUrl = window.location.href;
      const separator = fullUrl.includes('?') ? '&' : '?';
      
      if (!isLocalhost) {
        window.location.replace(fullUrl + separator + 'nocache=' + Date.now() + '&clear=1');
      } else {
        window.location.href = fullUrl + separator + 'nocache=' + Date.now();
      }
    }, 150);
  } else {
    // Cambiar a otro idioma
    console.log('✅ Aplicando traducción a:', lang);
    clearGoogleTranslateCookies();
    
    const googleTransCookie = '/es/' + lang;
    const hostname = window.location.hostname;
    
    setTimeout(() => {
      // Establecer cookie SOLO en el subdominio actual
      document.cookie = 'googtrans=' + googleTransCookie + '; path=/; max-age=3600';
      
      if (!isLocalhost) {
        // También en el subdominio específico
        document.cookie = 'googtrans=' + googleTransCookie + '; path=/; domain=' + hostname + '; max-age=3600';
        
        console.log('🍪 Cookie establecida para:', hostname, '=', googleTransCookie);
      }
      
      setTimeout(() => {
        // Mantener la URL completa incluyendo hash y parámetros
        const fullUrl = window.location.href;
        const separator = fullUrl.includes('?') ? '&' : '?';
        
        if (!isLocalhost) {
          window.location.replace(fullUrl + separator + 'lang=' + lang + '&t=' + Date.now());
        } else {
          window.location.href = fullUrl + separator + 'lang=' + lang + '&t=' + Date.now();
        }
      }, 150);
    }, 150);
  }
};

// Componente interno para acceder a useLocation
const AppContent = ({ selectedCategory, handleCategorySelect, handleBackToDashboard, resetCategory }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { getCurrentBranding } = useBranding();
  const isLoginPage = location.pathname === '/login' || !user;
  const currentBranding = getCurrentBranding();
  
  // Resetear categoría cuando cambia el usuario (login/logout)
  useEffect(() => {
    if (resetCategory) {
      resetCategory();
    }
  }, [user?.id, resetCategory]);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/diagnostic" element={<DiagnosticPage />} />
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      {selectedCategory ? (
                        <CategoryPage 
                          category={selectedCategory} 
                          onBack={handleBackToDashboard}
                          onCategoryChange={handleCategorySelect}
                        />
                      ) : (
                        <DashboardPage onCategorySelect={handleCategorySelect} />
                      )}
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute>
                      <UserManagementPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/sales" 
                  element={
                    <ProtectedRoute>
                      <SalesStatisticsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/products" 
                  element={
                    <ProtectedRoute>
                      <ProductManagementPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/categories" 
                  element={
                    <ProtectedRoute>
                      <CategoryManagementPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/access-history" 
                  element={
                    <ProtectedRoute>
                      <AccessHistoryPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/edit-history" 
                  element={
                    <ProtectedRoute>
                      <EditHistoryPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/settings" 
                  element={
                    <ProtectedRoute>
                      <SystemSettingsPage />
                    </ProtectedRoute>
                  } 
                />
              </Routes>
            </main>
            <Footer />
            <PopupManager />
            <TokenMigrationBanner />
            {/* Widget de WhatsApp flotante - oculto en login */}
            {!isLoginPage && currentBranding?.whatsapp_number && (
              <a
                href={`https://wa.me/${currentBranding.whatsapp_number}?text=${encodeURIComponent('¡Hola! ¿En qué podemos ayudarte?')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-6 right-6 z-[1000] flex items-center gap-3 bg-[#25D366] hover:bg-[#128C7E] text-white px-5 py-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] group"
                title="Contáctanos por WhatsApp"
              >
                <svg 
                  className="w-7 h-7" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="font-medium whitespace-nowrap hidden sm:inline group-hover:inline">
                  Contáctanos por WhatsApp
                </span>
              </a>
            )}
          </div>
  );
};

const App = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToDashboard = () => {
    setSelectedCategory(null);
  };
  
  const resetCategory = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <BrandingProvider>
          <Router>
            <SessionWarningModal />
            <AppContent 
              selectedCategory={selectedCategory}
              handleCategorySelect={handleCategorySelect}
              handleBackToDashboard={handleBackToDashboard}
              resetCategory={resetCategory}
            />
          </Router>
        </BrandingProvider>
      </SettingsProvider>
    </AuthProvider>
  );
};

export default App;